import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import {
  BLOOM_RADIUS,
  BLOOM_STRENGTH,
  BLOOM_THRESHOLD,
  FOG_DENSITY,
  PLANE_MAX_DIST,
  PLANE_MIN_DIST,
  SCENE_BG,
} from '../constants';
import { planeViewDirection } from '../utils/neuralSceneUtils';
import { pickPixelRatio } from './pickPixelRatio';
import { createPresentDitherPass } from './presentDitherPass';

export const INITIAL_DIST = 50;
/** Camera catch-up toward the coasting zoom target. */
export const ZOOM_DAMPING = 0.38;
/** Log-distance speed added per mouse-wheel notch. */
export const ZOOM_IMPULSE = 0.95;
/** How fast that speed dies out (1/seconds). */
export const ZOOM_FRICTION = 7.2;

/** Canvas, camera, composer, orbit controls, and host overlays. */
export function createSceneShell(container) {
  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  const hoverNameEl = document.createElement('div');
  hoverNameEl.className = 'neural-view__hover-name';
  hoverNameEl.setAttribute('aria-hidden', 'true');
  const hoverNameText = document.createElement('div');
  hoverNameText.className = 'neural-view__node-tag neural-view__hover-name-label';
  hoverNameEl.appendChild(hoverNameText);
  // Sit on the view shell, not the canvas host — labels must not change host size.
  const hoverLayer = container.parentElement || container;
  hoverLayer.appendChild(hoverNameEl);

  const labelRenderer = new CSS2DRenderer();
  labelRenderer.domElement.className = 'neural-view__label-layer';
  labelRenderer.domElement.style.pointerEvents = 'none';
  container.appendChild(labelRenderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SCENE_BG);
  scene.fog = new THREE.FogExp2(SCENE_BG, FOG_DENSITY);

  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 500);
  const viewDir = planeViewDirection();
  camera.position.copy(viewDir).multiplyScalar(INITIAL_DIST);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(pickPixelRatio(1, 1));
  renderer.toneMapping = THREE.NoToneMapping;
  const renderTarget = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 0 });
  const composer = new EffectComposer(renderer, renderTarget);
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD);
  const output = new OutputPass();
  const dither = createPresentDitherPass();
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(bloom);
  composer.addPass(output);
  composer.addPass(dither);

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = PLANE_MIN_DIST;
  controls.maxDistance = PLANE_MAX_DIST;
  controls.autoRotate = false;
  controls.enablePan = true;
  controls.screenSpacePanning = true;
  const basePolar = Math.PI / 2 - Math.atan(viewDir.y / viewDir.z);
  controls.minPolarAngle = basePolar - 0.42;
  controls.maxPolarAngle = basePolar + 0.42;
  controls.minAzimuthAngle = -0.5;
  controls.maxAzimuthAngle = 0.5;
  controls.enableZoom = false;
  controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
  controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_ROTATE };

  const nodeGroup = new THREE.Group();
  scene.add(nodeGroup);

  return {
    canvas,
    hoverNameEl,
    hoverNameText,
    labelRenderer,
    scene,
    camera,
    renderer,
    composer,
    bloom,
    output,
    dither,
    controls,
    motionQuery,
    nodeGroup,
    viewDir,
    INITIAL_DIST,
    MIN_DIST: PLANE_MIN_DIST,
    MAX_DIST: PLANE_MAX_DIST,
    ZOOM_DAMPING,
    ZOOM_IMPULSE,
    ZOOM_FRICTION,
  };
}
